import { Box, Card, CardActions, Grid, styled } from "@mui/material";
import { FEED_CARD_WIDTH } from "app/appConstants";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AxiosResponse } from "axios";
import { MprcContainer } from "components";
import { FeedContentLoadStatus } from "generated/CoreApi_v1";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CardContextType, CardLocalContent, CardOwnPropsOptions, CardSectionProps, PublicationFeed } from "views/knowledge/components/card";
import { useCardManagerStyles } from "views/knowledge/components/card/CardManagerStyles";
import FeedItemHelper from "views/knowledge/components/card/FeedItemHelper";
import { PrepareCardOwnProps } from "views/knowledge/components/card/PrepareCardOwnProps";
import { publish } from "views/knowledge/feed/FeedEvents";


export const CardContext = React.createContext<CardContextType>(undefined!);

export const useCardContext = () => React.useContext(CardContext);

const StyledGrid = styled(Grid)(
    ({ theme }) => (
        {
            ':empty': {
                display: "none"
            }
        }
    ));

interface IProps {
    dataRecord: PublicationFeed;
    otherInfo?: any;
    onDataItemChanged: (data: Partial<PublicationFeed>) => void;
    onNeedResetRowHeight: () => void;
    onReload: () => void;
    onFeedReload?: () => void;
    onMarkRead?: () => Promise<AxiosResponse<boolean, any>>;
}
type Props = IProps & CardSectionProps;

const CardBuilderInternal = (props: Props) => {
    const PriorityHeader = props.PriorityHeader ? props.PriorityHeader({}) : null;
    const Classification = props.Classification ? props.Classification({}) : null;
    const Icons = props.Icons ? props.Icons({}) : null;
    const ExternalLeftThumbnail = props.ExternalLeftThumbnail ? props.ExternalLeftThumbnail({}) : null;
    const LeftThumbnail = props.LeftThumbnail ? props.LeftThumbnail({}) : null;
    const RightThumbnail = props.RightThumbnail ? props.RightThumbnail({}) : null;
    const Author = props.Author ? props.Author({}) : null;
    const Title = props.Title ? props.Title({}) : null;
    const Content = props.Content ? props.Content({}) : null;
    const Media = props.Media ? props.Media({}) : null;
    const Attach = props.Attach ? props.Attach({}) : null;
    const Task = props.Task ? props.Task({}) : null;
    const Footer = props.Footer ? props.Footer({}) : null;
    const MediaHeader = props.MediaHeader ? props.MediaHeader({}) : null;

    return (
        <MprcContainer>
            <Card>
                <Box sx={{ minWidth: FEED_CARD_WIDTH }}>
                    <Grid container width="100%" direction="column" gap={1} padding={1}>

                        {
                            MediaHeader &&
                            <StyledGrid id="mediaHeaderSection" width="100%">
                                {MediaHeader}
                            </StyledGrid>
                        }

                        {
                            (PriorityHeader || Classification || Icons) &&
                            <Grid width="100%" flexWrap="nowrap">
                                <Grid container gap={1} flexWrap="nowrap">
                                    <Grid size="grow">
                                        <Grid container direction="column">
                                            {
                                                PriorityHeader &&
                                                <StyledGrid id="priorityHeaderSection">{PriorityHeader}</StyledGrid>
                                            }
                                            {
                                                Classification &&
                                                <StyledGrid id="classificationSection" width="100%">{Classification}</StyledGrid>
                                            }
                                        </Grid>
                                    </Grid>
                                    {
                                        Icons &&
                                        <Grid id="iconsSection">
                                            {Icons}
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        }

                        {
                            (ExternalLeftThumbnail || LeftThumbnail || RightThumbnail || Author || Title || Content) &&
                            <Grid width="100%">
                                <Grid container gap={1}>
                                    {
                                        ExternalLeftThumbnail &&
                                        <Grid id="externalImageLeft">
                                            {ExternalLeftThumbnail}
                                        </Grid>
                                    }
                                    {
                                        (LeftThumbnail || RightThumbnail || Author || Title || Content) &&
                                        <Grid size="grow" container direction="column" gap={1} width={ExternalLeftThumbnail ? "70%" : "100%"}>
                                            {
                                                Title &&
                                                <StyledGrid id="titleSection" width="100%">{Title}</StyledGrid>
                                            }

                                            {(LeftThumbnail || RightThumbnail || Author || Content) &&
                                                <Grid width="100%">
                                                    <Grid container gap={1} justifyContent="center">
                                                        {
                                                            LeftThumbnail &&
                                                            <Grid id="thumbLeftSection">{LeftThumbnail}</Grid>
                                                        }

                                                        <Grid size="grow" container direction="column" gap={1}>
                                                            {
                                                                Author &&
                                                                <StyledGrid id="authorSection" width="100%">{Author}</StyledGrid>
                                                            }
                                                            {
                                                                Content &&
                                                                <StyledGrid id="contentSection" className={AppGlobalClassKeys.qlContentSection} width="100%">{Content}</StyledGrid>
                                                            }
                                                        </Grid>

                                                        {
                                                            RightThumbnail &&
                                                            <Grid id="thumbRightSection" >{RightThumbnail}</Grid>
                                                        }
                                                    </Grid>
                                                </Grid>
                                            }
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        }

                        {
                            Media &&
                            <StyledGrid id="mediaSection" width="100%">
                                {Media}
                            </StyledGrid>
                        }

                        {
                            Attach &&
                            <StyledGrid id="attachSection" width="calc(100% - 2px)">
                                {Attach}
                            </StyledGrid>
                        }

                        {
                            Task &&
                            <StyledGrid id="taskSection" width="100%">
                                {Task}
                            </StyledGrid>
                        }

                    </Grid>
                </Box>
                <CardActions>
                    {
                        Footer &&
                        <Grid id="footerSection" width="100%">
                            {Footer}
                        </Grid>
                    }
                </CardActions>
            </Card>
        </MprcContainer>
    );
};

export const CardBuilder = (props: Props) => {
    const divRef = useRef<HTMLDivElement | null>(null);
    const feedItemHelper = useRef<FeedItemHelper>(new FeedItemHelper(props.dataRecord));
    const contentLoadStatus = (props.dataRecord.moderateReport?.comment?.contentLoadStatus ?? props.dataRecord.contentLoadStatus);

    const { classes } = useCardManagerStyles();
    const [cardOwnProps, setCardOwnProps] = useState<CardOwnPropsOptions>(PrepareCardOwnProps(props.dataRecord, feedItemHelper.current));
    const [localContent, setContent] = useState<CardLocalContent>({
        content: props.dataRecord.moderateReport?.comment?.content ?? props.dataRecord.content,
        contentLoadStatus: contentLoadStatus,
        state: contentLoadStatus == FeedContentLoadStatus.LoadContentInDemand ? "contracted" : undefined
    });

    /**
     * Modifica a altura do CARD se o usuário expande ou contrai o texto da postagem.
     */
    useEffect(() => {
        props.onNeedResetRowHeight();
    }, [localContent.state]);

    /**
     * RENDER
     */
    const memoizedCard = useMemo(() => {
        return <CardBuilderInternal {...props} />;
    }, [props.dataRecord]);

    return (
        <div
            ref={divRef}
            onMouseEnter={() => divRef.current?.classList.add(classes.cardMouseOver)}
            onMouseLeave={() => divRef.current?.classList.remove(classes.cardMouseOver)}
        >
            <CardContext.Provider value={
                {
                    cardDivRef: divRef.current,
                    pub: props.dataRecord,
                    otherInfo: props.otherInfo,
                    cardOwnProps: cardOwnProps,
                    localContent: localContent,
                    helper: feedItemHelper.current,
                    onMarkRead: props.onMarkRead,
                    onReload: props.onReload,
                    onFeedReload: props.onFeedReload,
                    onFeedItemChanged: props.onDataItemChanged,
                    onUpdateLocalContent: (data: CardLocalContent) => setContent(data),
                    onUpdateCardOwnProps: (data: Partial<CardOwnPropsOptions>) =>
                        setCardOwnProps((state) => {
                            const newState = { ...state, ...data };
                            publish("CardOwnPropsChanged", JSON.stringify(newState));
                            return newState;
                        })
                }}>
                {memoizedCard}
            </CardContext.Provider>
        </div>
    );
};
